import { useEffect, useState } from "react";
import { useContext } from "react";
import SkipPreviousIcon from "@mui/icons-material/SkipPrevious";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import { IconButton, Slider } from "@mui/material";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { serverUrl, socket } from "../App";
import StateContext from "../StateContext";
import {
  RequestCurrentMusicChange,
  RequestCurrentMusicPause,
  RequestCurrentMusicPlay,
} from "../types/clientRequest.type";
import { Music, Playlist } from "../types/global.type";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import { RequestFactory } from "../libs/services";

export const MusicPlayer = () => {
  const [shakePrev, setShakePrev] = useState(false);
  const [shakeNext, setShakeNext] = useState(false);
  const [shakePlay, setShakePlay] = useState(false);
  const [sliderValue, setSliderValue] = useState(0.5);
  const [currentTimeCode, setCurrentTimeCode] = useState(0);

  const baseMusicUrl = `${serverUrl}/musics`;
  const context = useContext(StateContext);

  const {
    audioRef,
    playMusic,
    currentMusic,
    currentPlaylist,
    setCurrentMusic,
    setPlayMusic,
    volume,
    setVolume,
  } = context;

  useEffect(() => {
    const handleAudioEnd = () => {
      if (!currentMusic || !currentPlaylist) return;
      const indexMusic = getIndexMusicInPlaylist(currentMusic, currentPlaylist);
      const nextMusic = currentPlaylist?.musics[indexMusic + 1];
      nextMusic && setCurrentMusic(nextMusic);
      setPlayMusic(true);
    };
    if (audioRef.current) {
      audioRef.current.addEventListener("ended", handleAudioEnd);
    }
  }, [audioRef, currentMusic, currentPlaylist, setCurrentMusic, setPlayMusic]);

  const handlePrevMusic = () => {
    if (!currentMusic || !currentPlaylist) return;
    const indexMusic = getIndexMusicInPlaylist(currentMusic, currentPlaylist);
    const prevMusic = currentPlaylist?.musics[indexMusic - 1];
    if (indexMusic > -1 && prevMusic) {
      const data: RequestCurrentMusicChange = {
        target: "CURRENT_MUSIC",
        request: "CHANGE",
        payload: {
          musicId: prevMusic.id,
        },
      };
      socket.emit("message", data);
    } else {
      setShakePrev(true);
      setTimeout(() => setShakePrev(false), 500);
    }
  };

  const handleNextMusic = () => {
    if (!currentMusic || !currentPlaylist) return;
    const indexMusic = getIndexMusicInPlaylist(currentMusic, currentPlaylist);
    const nextMusic = currentPlaylist?.musics[indexMusic + 1];
    if (indexMusic > -1 && nextMusic) {
      const data: RequestCurrentMusicChange = {
        target: "CURRENT_MUSIC",
        request: "CHANGE",
        payload: {
          musicId: nextMusic.id,
        },
      };
      socket.emit("message", data);
    } else {
      setShakeNext(true);
      setTimeout(() => setShakeNext(false), 200);
    }
  };

  const handlePlay = (newPlayMusic: boolean) => {
    if (!currentMusic) {
      setShakePlay(true);
      setTimeout(() => setShakePlay(false), 200);
      return;
    }
    if (newPlayMusic) {
      const dataPlay: RequestCurrentMusicPlay = {
        target: "CURRENT_MUSIC",
        request: "PLAY",
        payload: {
          musicTimeCode: audioRef.current?.currentTime ?? 0,
        },
      };
      socket.emit("message", dataPlay);
    } else {
      const dataPause: RequestCurrentMusicPause = {
        target: "CURRENT_MUSIC",
        request: "PAUSE",
        payload: {
          musicTimeCode: audioRef.current?.currentTime ?? 0,
        },
      };
      socket.emit("message", dataPause);
    }
  };

  function exponentialGrowth(x: number) {
    const y = Math.pow(x, 3);
    return y;
  }

  useEffect(() => {
    audioRef.current!.ontimeupdate = () => {
      setCurrentTimeCode(audioRef.current?.currentTime ?? 0);
    };
  }, [audioRef]);

  function formatTime(time: number) {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time - minutes * 60);
    return `${minutes}:${seconds < 10 ? "0" + seconds : seconds}`;
  }

  return (
    <div className="music-container">
      <div className="container-image-music">
        <div className="music-title">{currentMusic?.title}</div>
        <img src={currentMusic?.thumbail} alt="" />
        <div className="hover-control-music">
          <div className="timecode-container">
            {formatTime(currentTimeCode)}
          </div>
          <Slider
            aria-label="timecode-slider"
            value={currentTimeCode}
            onChange={(event: Event, newValue: number | number[]) => {
              if (typeof newValue === "number" && isFinite(newValue)) {
                audioRef.current!.currentTime = newValue;
              }
            }}
            min={0}
            max={audioRef.current?.duration ?? 0}
            step={1}
            style={{ color: "white", flex: 8 }}
            className="timecode-slider"
            onChangeCommitted={(event, value) => {
              setCurrentTimeCode(value as number);
              RequestFactory.currentMusic.newTimeCode(
                audioRef.current!.currentTime,
                socket.id
              );
            }}
          />
          <div className="volume-container">
            <div className="volume-sub-container">
              <Slider
                aria-label="Volume"
                value={sliderValue}
                onChange={(event: Event, newValue: number | number[]) => {
                  setSliderValue(newValue as number);
                  const volume = exponentialGrowth(newValue as number);
                  audioRef.current!.volume = volume;
                  setVolume(volume);
                }}
                min={0}
                max={1}
                step={0.01}
                style={{
                  color: "white",
                }}
                orientation="vertical"
                className="volume-slider"
              />
              <VolumeUpIcon sx={{ color: "white", zoom: "1.5" }} />
            </div>
          </div>
        </div>
      </div>

      <audio
        style={{ display: "none" }}
        src={baseMusicUrl + "/" + currentMusic?.id + ".mp3"}
        ref={audioRef}
        autoPlay={playMusic}
      />
      <div className="music-controls">
        <IconButton onClick={() => handlePrevMusic()} size="large">
          <SkipPreviousIcon
            className={"previous-icon" + (shakePrev ? " shake-animation" : "")}
            fontSize="large"
          />
        </IconButton>

        <IconButton onClick={() => handlePlay(!playMusic)} size="large">
          {playMusic ? (
            <PauseIcon
              className={"play-icon" + (shakePlay ? " shake-animation" : "")}
              fontSize="large"
            />
          ) : (
            <PlayArrowIcon
              className={"play-icon" + (shakePlay ? " shake-animation" : "")}
              fontSize="large"
            />
          )}
        </IconButton>

        <IconButton onClick={() => handleNextMusic()} size="large">
          <SkipNextIcon
            className={"next-icon" + (shakeNext ? " shake-animation" : "")}
            fontSize="large"
          />
        </IconButton>
      </div>
    </div>
  );
};

function getIndexMusicInPlaylist(music: Music, playlist: Playlist) {
  return playlist.musics.findIndex((m) => m.id === music.id);
}
